import { useEffect, useMemo, useState } from 'react';
import {
  useListPtoPprFiguresQuery,
  useRegeneratePtoPprTkSectionMutation,
  useUpdatePtoPprTkSectionMutation,
  type PtoPprFigure,
  type PtoPprFinding,
  type PtoPprTkDraft,
  type PtoPprTkSection,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Actions, Banner, Muted, Tag, errorText } from '../kfo/ui';
import { CitationChips, FindingItem, PprTableView, SectionText } from './DocParts';
import { FiguresBlock } from './FiguresBlock';

/** Приложения, которые программа строит по РД (§8.3–8.5). */
const GENERATED_APPENDICES = ['app_rigging', 'app_labor', 'app_schedule', 'app_sgp'];
import { canRegenerate, sectionAnchor, sectionHeading, sourceMeta } from './model';
import { EditArea, InlineBox, SectionBlock, SectionHead, SectionTitle, Small, WarnBox } from './ui';

/**
 * Вкладка «Разделы» черновика rd: текст по шаблону ППР с номерами, таблицы из
 * данных, ссылки-чипы на исходные данные, предупреждения проверки кода и
 * замечания аудитора к разделу. «Править» — ручная правка текста (таблицы
 * собираются кодом и не редактируются); «Пересобрать» — ИИ заново, с уточнением.
 * У текстовых разделов и разделов «за инженером» — рисунки (графическая часть):
 * свои чертежи инженера и схемы программы.
 */
export function SectionsTab({
  draft,
  canWrite,
  jumpKey,
  onJumped,
}: {
  draft: PtoPprTkDraft;
  canWrite: boolean;
  /** Раздел, к которому прокрутить (переход из замечания аудитора). */
  jumpKey: string | null;
  onJumped: () => void;
}) {
  const [editKey, setEditKey] = useState('');
  const [editText, setEditText] = useState('');
  const [regenKey, setRegenKey] = useState('');
  const [hint, setHint] = useState('');
  const [error, setError] = useState('');
  const [flashKey, setFlashKey] = useState('');
  const [updateSection, { isLoading: saving }] = useUpdatePtoPprTkSectionMutation();
  const [regenerate, { isLoading: regenerating }] = useRegeneratePtoPprTkSectionMutation();
  const { data: figuresData } = useListPtoPprFiguresQuery(draft.id);

  // Схема производства работ: в ТК — раздел 7, в ППР — технология (рисунок).
  const schemeKey = draft.sections.some((s) => s.key === 'app_scheme') ? 'app_scheme' : 'main';

  const figuresBySection = useMemo(() => {
    const m = new Map<string, PtoPprFigure[]>();
    for (const f of figuresData?.items ?? []) {
      m.set(f.section_key, [...(m.get(f.section_key) ?? []), f]);
    }
    return m;
  }, [figuresData]);

  const findingsBySection = useMemo(() => {
    const m = new Map<string, PtoPprFinding[]>();
    for (const f of draft.review?.findings ?? []) {
      if (!f.section_key) continue;
      m.set(f.section_key, [...(m.get(f.section_key) ?? []), f]);
    }
    return m;
  }, [draft.review]);

  useEffect(() => {
    if (!jumpKey) return;
    document
      .getElementById(sectionAnchor(draft.id, jumpKey))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlashKey(jumpKey);
    onJumped();
  }, [jumpKey, draft.id, onJumped]);

  useEffect(() => {
    if (!flashKey) return;
    const t = window.setTimeout(() => setFlashKey(''), 1800);
    return () => window.clearTimeout(t);
  }, [flashKey]);

  async function save(key: string) {
    setError('');
    try {
      await updateSection({ draftId: draft.id, key, content: editText }).unwrap();
      setEditKey('');
      setEditText('');
    } catch (e) {
      setError(errorText(e, 'Не удалось сохранить раздел.'));
    }
  }

  async function rebuild(key: string) {
    setError('');
    try {
      await regenerate({ draftId: draft.id, key, hint: hint.trim() || undefined }).unwrap();
      setRegenKey('');
      setHint('');
    } catch (e) {
      setError(errorText(e, 'Не удалось пересобрать раздел.'));
    }
  }

  function renderSection(s: PtoPprTkSection) {
    const meta = sourceMeta(s.source);
    const editing = editKey === s.key;
    const regen = regenKey === s.key;
    const findings = findingsBySection.get(s.key) ?? [];
    const warnings = s.edited ? [] : s.warnings;
    return (
      <SectionBlock
        key={s.key}
        id={sectionAnchor(draft.id, s.key)}
        data-section={s.key}
        $level={s.level}
        $flash={flashKey === s.key}
      >
        <SectionHead>
          <SectionTitle $level={s.level}>{sectionHeading(s)}</SectionTitle>
          <Tag $tone={meta.tone}>{meta.label}</Tag>
          {s.edited && <Tag $tone="info">изменён инженером</Tag>}
          {s.status === 'failed' && <Tag $tone="danger">не сгенерирован</Tag>}
          {s.status === 'pending' && <Tag $tone="neutral">в работе</Tag>}
          {canWrite && !editing && !regen && (
            <Actions>
              <Button
                $variant="ghost"
                $size="sm"
                type="button"
                onClick={() => {
                  setRegenKey('');
                  setEditKey(s.key);
                  setEditText(s.content);
                }}
              >
                Править
              </Button>
              {canRegenerate(s) && (
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  onClick={() => {
                    setEditKey('');
                    setRegenKey(s.key);
                    setHint('');
                  }}
                >
                  Пересобрать
                </Button>
              )}
            </Actions>
          )}
        </SectionHead>

        {editing ? (
          <InlineBox>
            <EditArea
              $rows={12}
              value={editText}
              aria-label="Текст раздела"
              onChange={(e) => setEditText(e.target.value)}
            />
            <Small>
              Абзац — с новой строки, пункт списка — «- » в начале строки.
              {s.tables.length > 0 ? ' Таблицы раздела собираются из данных и не правятся.' : ''}
              {draft.review?.status === 'approved'
                ? ' После правки документ потребует повторной проверки аудитором.'
                : ''}
            </Small>
            <Actions>
              <Button
                $variant="primary"
                $size="sm"
                type="button"
                disabled={saving}
                onClick={() => save(s.key)}
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button $variant="ghost" $size="sm" type="button" onClick={() => setEditKey('')}>
                Отмена
              </Button>
            </Actions>
          </InlineBox>
        ) : (
          <SectionText content={s.content} />
        )}

        {s.tables.map((t, i) => (
          <PprTableView key={i} table={t} />
        ))}

        {(s.source === 'engineer' || s.source === 'llm' || s.key.startsWith('app_')) && (
          <FiguresBlock
            draftId={draft.id}
            sectionKey={s.key}
            figures={figuresBySection.get(s.key) ?? []}
            canWrite={canWrite}
            engineer={s.source === 'engineer'}
            scheme={s.key === schemeKey || GENERATED_APPENDICES.includes(s.key)}
          />
        )}

        <CitationChips items={s.citations} draft={draft} />

        {warnings.length > 0 && (
          <WarnBox data-warnings={s.key}>
            <b>Проверить:</b>
            <ul>
              {warnings.map((w, i) => (
                <li key={i}>{w.text}</li>
              ))}
            </ul>
          </WarnBox>
        )}

        {findings.map((f, i) => (
          <FindingItem key={i} finding={f} draft={draft} />
        ))}

        {regen && (
          <InlineBox>
            <Small>
              Что исправить? ИИ перепишет раздел по паспорту РД с учётом уточнения, затем аудитор
              проверит документ заново.
            </Small>
            <EditArea
              $rows={3}
              value={hint}
              aria-label="Что исправить"
              placeholder="например: добавить последовательность погружения свай по захваткам"
              onChange={(e) => setHint(e.target.value)}
            />
            <Actions>
              <Button
                $variant="primary"
                $size="sm"
                type="button"
                disabled={regenerating}
                onClick={() => rebuild(s.key)}
              >
                {regenerating ? 'Запуск…' : 'Пересобрать раздел'}
              </Button>
              <Button $variant="ghost" $size="sm" type="button" onClick={() => setRegenKey('')}>
                Отмена
              </Button>
            </Actions>
          </InlineBox>
        )}
      </SectionBlock>
    );
  }

  return (
    <div>
      {error && <Banner $tone="danger">{error}</Banner>}
      {draft.sections.length === 0 ? (
        <Muted>Разделов нет.</Muted>
      ) : (
        draft.sections.map(renderSection)
      )}
    </div>
  );
}
